import { QueryClient } from '@tanstack/react-query';
import { GlobalPermission } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/user_pb';
import { LoaderFunctionArgs, redirect } from 'react-router';
import { System } from '@wavingroup/aqora-v2-crate/wavin/aqora/v2/system_pb';
import { bomQuery } from '~/pages/BillOfMaterials/api/bom.queries';
import { BillOfMaterialsModel } from '~/pages/BillOfMaterials/models/BillOfMaterialsModel';
import {
  BOMParameters,
  CreateBomParametersModel,
} from '~/pages/BillOfMaterials/models/CreateBomParametersModel';
import { SystemDesignAlertsModel } from '~/shared/models/system/SystemDesignAlertsModel';
import { getSystemQuery } from '~/shared/api/system.queries';
import { listSystemDesignAlertsQuery } from '~/shared/api/systemDesignAlerts.queries';
import {
  checkAuthorization,
  requireUserGlobalPermission,
} from '~/shared/auth/auth-utils';
import { createSystemRoute } from '~/shared/models/create-routes';
import { assertIsDefined } from '~/types/assert-type';
import { SystemModel } from '~/shared/models/system/SystemModel';

export type BillOfMaterialsPageLoaderResult = {
  system: System;
  billOfMaterials: BillOfMaterialsModel | null;
  systemDesignAlerts: SystemDesignAlertsModel;
  bomParameters: BOMParameters;
};

export const billOfMaterialsPageLoader =
  (queryClient: QueryClient) =>
  async ({
    params,
    request,
  }: LoaderFunctionArgs): Promise<
    BillOfMaterialsPageLoaderResult | Response
  > => {
    await checkAuthorization(queryClient, request);

    const { systemId } = params;

    assertIsDefined(systemId);

    const getSystemResponse = await queryClient.ensureQueryData(
      getSystemQuery(systemId),
    );
    const system = new SystemModel({ system: getSystemResponse });

    const systemNotInDesignPhase =
      system.state !== 'CONFIGURATION' && system.state !== 'ADVICE';

    if (systemNotInDesignPhase) {
      return redirect(createSystemRoute(system.id));
    }

    await requireUserGlobalPermission(
      queryClient,
      request,
      GlobalPermission.WAVIN_ONLY,
    );

    const systemDesignAlerts = await queryClient.ensureQueryData(
      listSystemDesignAlertsQuery(system.name),
    );

    let resolvedBomParameters = CreateBomParametersModel.getDefault(system);
    let billOfMaterials: BillOfMaterialsModel | null = null;

    if (!systemDesignAlerts.shouldBlockSystemStateChange) {
      billOfMaterials = await queryClient.fetchQuery(
        bomQuery(resolvedBomParameters, system),
      );

      const updatedBomParameters =
        CreateBomParametersModel.enableConnectorToolIfNeeded(
          resolvedBomParameters,
          system,
          billOfMaterials,
        );

      if (updatedBomParameters) {
        resolvedBomParameters = updatedBomParameters;
        billOfMaterials = await queryClient.fetchQuery(
          bomQuery(resolvedBomParameters, system),
        );
      }
    }

    return {
      system: getSystemResponse,
      billOfMaterials,
      systemDesignAlerts,
      bomParameters: resolvedBomParameters,
    };
  };
